import {ShapeLineStyle, ShapeType, ShapeWidgetData} from '@dv5/domain'
import React, {CSSProperties, FC, useEffect, useRef, useState} from 'react'
import {isModPressed} from '@dv5/components'
import {ColorPicker} from '../common/ColorPicker'
import {SHAPE_COLORS} from "./constants"
import {ShapePicker} from "./ShapePicker"
import {useShapeWidgetUpdate} from "./useShapeWidgetUpdate"
import {ShapeLineStylePicker} from './ShapeLineStylePicker'
import './ShapeWidgetFullEditor.less'

interface Props {
    canvasId: string
    widgetId: string
    data: ShapeWidgetData
    toolbarStyles: CSSProperties
    onClose: () => void
}

export const ShapeWidgetFullEditor: FC<Props> = ({
    canvasId,
    widgetId,
    data,
    toolbarStyles,
    onClose,
}) => {
    const { updateData } = useShapeWidgetUpdate(canvasId, widgetId, data)
    const [editingText, setEditingText] = useState(data.text ?? '')
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        setEditingText(data.text ?? '')
    }, [data.text])

    const handleFillColorChange = (color: string) => {
        updateData(['bgColor'], {bgColor: color})
    }

    const handleFgColorChange = (color: string) => {
        updateData(['fgColor'], {fgColor: color})
    }

    const handleBorderColorChange = (color: string) => {
        updateData(['borderColor'], {borderColor: color})
    }

    const handleShapeTypeChange = (shapeType: ShapeType) => {
        updateData(['shapeType'], {shapeType})
    }

    const handleLineStyleChange = (lineStyle: ShapeLineStyle) => {
        updateData(['lineStyle'], {lineStyle})
    }

    const handleBorderWidthChange = (event: React.MouseEvent<HTMLButtonElement>, delta: number) => {
        event.preventDefault()
        event.stopPropagation()
        const newWidth = Math.max(0, Math.min(12, (data.borderWidth != null ? data.borderWidth : 1) + delta))
        updateData(['borderWidth'], {borderWidth: newWidth})
    }

    const saveText = () => {
        if (editingText !== (data.text ?? '')) {
            updateData(['text'], {text: editingText})
        }
    }

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditingText(e.target.value)
    }

    const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'Enter' && isModPressed(e)) {
            e.preventDefault()
            saveText()
        }
    }

    const closeEditor = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        event.preventDefault()
        onClose()
    }

    return (
        <div className='shape-full-editor'>
            <div className='shape-full-editor__header'>
                <span className='shape-full-editor__title'>Shape Properties</span>
                <button
                    className='shape-full-editor__close'
                    onMouseDown={closeEditor}
                    aria-label='Close full editor'
                    type='button'
                >
                    ×
                </button>
            </div>

            <div className='shape-full-editor__sections'>
                <div className='shape-full-editor__section' title='Shape'>
                    <ShapePicker
                        prefix="shape-full-editor"
                        value={data.shapeType}
                        onChange={handleShapeTypeChange}
                        columns={4}
                    />
                </div>

                <div className='shape-full-editor__section' title='Text'>
                    <textarea
                        ref={textareaRef}
                        className='shape-full-editor__textarea'
                        value={editingText}
                        onChange={handleTextChange}
                        onKeyDown={handleTextKeyDown}
                        onBlur={saveText}
                        rows={3}
                    />
                </div>

                <div className='shape-full-editor__section' title='Fill Color'>
                    <ColorPicker
                        value={data.bgColor}
                        presets={SHAPE_COLORS}
                        onChange={handleFillColorChange}
                    />
                </div>

                <div className='shape-full-editor__section' title='Text Color'>
                    <ColorPicker
                        value={data.fgColor}
                        presets={SHAPE_COLORS}
                        onChange={handleFgColorChange}
                    />
                </div>

                <div className='shape-full-editor__section' title='Border Color'>
                    <ColorPicker
                        value={data.borderColor}
                        presets={SHAPE_COLORS}
                        onChange={handleBorderColorChange}
                    />
                </div>

                <div className='shape-full-editor__section' title='Line Style'>
                    <ShapeLineStylePicker value={data.lineStyle} onChange={handleLineStyleChange} />
                </div>

                <div className='shape-full-editor__section' title='Border Width'>
                    <div className='shape-full-editor__border-width'>
                        <button
                            className='shape-full-editor__button'
                            onMouseDown={event => handleBorderWidthChange(event, -1)}
                            aria-label='Decrease border width'
                            type='button'
                    disabled={(data.borderWidth != null ? data.borderWidth : 1) <= 0}
                >
                    −
                </button>
                <span className='shape-full-editor__border-value'>{data.borderWidth != null ? data.borderWidth : 1}</span>
                        <button
                            className='shape-full-editor__button'
                            onMouseDown={event => handleBorderWidthChange(event, 1)}
                            aria-label='Increase border width'
                            type='button'
                            disabled={(data.borderWidth != null ? data.borderWidth : 1) >= 12}
                        >
                            +
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
